// src/pages/HomePage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getDoc, query, where, doc, orderBy } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../services/firebase';
import { FaArrowRight, FaPlus, FaSearch, FaCalendarAlt } from 'react-icons/fa';
import { requestNotificationPermission } from "../services/firebaseNotification";

import PreferredTimeDisplay from '../components/PreferredTimeDisplay';
import { useVerifiedAuth } from "../components/hooks/useVerifiedAuth";
import '../styles/HomePage.css';

function HomePage() {
  const { user, loading: authLoading } = useVerifiedAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();


  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        // Get user profile
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.exists() ? userDoc.data() : {};

        if (!userData.displayName) {
          navigate("/firstprofile");
          return;
        }

        // Get activities
        const activitiesRef = collection(db, "search_activities");
        const q = query(
          activitiesRef,
          where("ownerId", "==", user.uid),
          where("status", "in", ["pending", "matched", "confirmed", "finished"])
        );
        const snapshot = await getDocs(q);
        const fetched = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());

        setActivities(fetched);
      } catch (error) {
        console.error("Error fetching search activities:", error);
      } finally {
        setLoading(false);
      }
    };

    requestNotificationPermission();
    fetchData();
  }, [user, navigate]);


  const handleCardOpen = (id) => navigate(`/search_detail/${id}`);

  return (
    <>
      {/* Fixed full-page gradient backdrop */}
      <div className="hp-bg" aria-hidden="true" />

      <div className="hp-page">
        {/* Hero (no stats) */}
        <header className="hp-hero">
          <div className="hp-hero-content">
            <div>
              <h1 className="hp-title">Your Active Searches</h1>
              <p className="hp-subtitle">
                Meet new people in real life.
              </p>
              {process.env.REACT_APP_ENV !== 'PROD' && (
                <p className="hp-subtitle">({process.env.REACT_APP_ENV})</p>
              )}
            </div>
            <button
              className="btn btn-gradient hp-cta"
              onClick={() => navigate('/create_search')}
              aria-label="Create a new search"
            >
              <FaPlus className="me-2" />
              New Search
            </button>
          </div>
        </header>

        {/* Content Area */}
        <main className="hp-content">
          {loading ? (
            <div className="hp-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="hp-card hp-skeleton" key={i}>
                  <div className="hp-skel-line w-60"></div>
                  <div className="hp-skel-line w-40"></div>
                  <div className="hp-skel-chip"></div>
                </div>
              ))}
            </div>
          ) : activities.length === 0 ? (
            <EmptyState onCreate={() => navigate('/create_search')} />
          ) : (
            <div className="hp-grid">
              {activities.map((activity) => (
                <article
                  key={activity.id}
                  className="hp-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCardOpen(activity.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCardOpen(activity.id);
                  }}
                  aria-label={`Open search ${activity.activityType || 'Activity'}`}
                >
                  <div className="hp-card-top">
                    <div className="hp-card-icon">
                      <FaSearch />
                    </div>
                    <StatusPill status={activity.status} />
                  </div>

                  <h3 className="hp-card-title">
                    {activity.activityType || 'Activity'}
                  </h3>

                  <div className="hp-meta">
                    <div className="hp-meta-row">
                      <FaCalendarAlt className="hp-meta-icon" />
                      <PreferredTimeDisplay preferredTime={activity.preferredTime} />
                    </div>
                  </div>

                  <div className="hp-card-cta">
                    <span className="hp-link">View details</span>
                    <FaArrowRight className="hp-chevron" />
                  </div>
                </article>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
}

function StatusPill({ status }) {
  const label = (status || '').toLowerCase();
  return <span className={`hp-pill ${label}`}>{label || 'unknown'}</span>;
}

function EmptyState({ onCreate }) {
  return (
    <section className="hp-empty">
      <div className="hp-empty-illustration" aria-hidden="true">
        <svg width="140" height="120" viewBox="0 0 140 120" fill="none">
          <defs>
            <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--hp-grad-a)" />
              <stop offset="100%" stopColor="var(--hp-grad-b)" />
            </linearGradient>
          </defs>
          <rect x="15" y="20" rx="12" ry="12" width="110" height="75" fill="url(#g)" opacity="0.25" />
          <rect x="25" y="30" rx="8" ry="8" width="90" height="10" fill="url(#g)" opacity="0.35" />
          <rect x="25" y="50" rx="8" ry="8" width="60" height="10" fill="url(#g)" opacity="0.35" />
          <circle cx="110" cy="85" r="14" stroke="url(#g)" strokeWidth="3" fill="none" />
          <line x1="120" y1="95" x2="130" y2="105" stroke="url(#g)" strokeWidth="3" />
        </svg>
      </div>
      <h2 className="hp-empty-title">No active searches yet</h2>
      <p className="hp-empty-subtitle">
        Start your first search to find a match. We’ll keep it here for quick access.
      </p>
      <button className="btn btn-gradient" onClick={onCreate}>
        <FaPlus className="me-2" />
        Create Search
      </button>
    </section>
  );
}

export default HomePage;